import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import { TableHead, TextField, Chip, Checkbox } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useGetUsersQuery } from '../../features/users/usersApiSlice';
import { memo } from 'react';
import PulseLoader from 'react-spinners/PulseLoader';
import { useLocation, Navigate, Outlet } from "react-router-dom";
import nameStringFn from "../../data/nameStringFn";
import {HeadingWrap, Span, CommonButton } from '../../Styles/CommonStyles';
import {TableContStyle, TableHeadStyle } from '../../Styles/TableStyles';
import { useGetdesignationsQuery } from '../../features/designations/designationApiSlice';
import DetailsIcon from '@mui/icons-material/Details';
import InventoryIcon from '@mui/icons-material/Inventory';
import UserdetailsModal from './UserdetailsModal';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useGetofficesQuery } from '../../features/offices/officesApiSlice';
import useAuth from '../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import ExcelDownload from '../../data/exceldownload';
import UserAssetsModal from './UserAssetsModal';


const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Button2Div =styled.div`
  display: flex;
  justify-content: end;
`

function TablePaginationActions(props) {
  const start = performance.now();

  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );

}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const tableFontSize = 18;

// The user Roles Array function creates an array of all roles so that they can be displayed as chips in the table.
const userRolesArray = (userObject) => {
  if (!userObject?.roles) return [];
  
  let rolesArray = [];
  const mainRoleArray = userObject.roles.main || [];
  let officeRoleArray = [];
  
  for(let key in userObject.roles){
    if(key !== "main" && userObject.roles[key]){
      for(let val of userObject.roles[key]){
        if (val) officeRoleArray.push(`${val} (${key})`)
      }
    }
  }
  
  return mainRoleArray.concat(officeRoleArray);
}


//The findSubordinateOffices function gives an array of objects of all offices which are subordinate to officeName.
const findSubordinateOffices = (offices, officeName, result = []) => {
  if (!offices || !officeName) return result;
  
  const subOffices = offices.filter(office => office?.superioroffice === officeName);
  
  for (const subOffice of subOffices) {
    if (subOffice) {
      result.push(subOffice);
      findSubordinateOffices(offices, subOffice.name, result);
    }
  }
  
  return result;
}

//The filterFunctionUser gives the array of filtered array of users as per roles available.
const filterFunctionUser = (userArray, Auth, presentOffice, officeArray) => {
  if (!userArray || !userArray.length || !Auth) return [];
  
  let tempArray = [...userArray]; // Proper cloning of array
  
  const rolesArray = Auth.roles?.[presentOffice] || [];
  
  // Admin or level1/level2 approvers can see all users
  if (Auth.roles?.["main"]?.includes("Admin") || rolesArray.some(item => ["level1approver", "level2approver"].includes(item))) {
    return userArray;
  }

  // If user has 'Bo' role but is not a 'UnitHead'
  if (rolesArray.includes("Bo") && !rolesArray.includes("UnitHead")) {
    tempArray = tempArray.filter(user => {
      if (!user?.office) return false; // Ensure office exists

      // Extract mainCharge and additionalCharge
      const mainCharge = user.office.mainCharge || '';
      const additionalCharge = user.office.additionalCharge || [];
      const userOffices = [mainCharge, ...additionalCharge]; // Combine both

      return userOffices.includes(presentOffice);
    });
    return tempArray;
  }

  // If user is 'UnitHead', filter based on subordinate offices
  if (rolesArray.includes("UnitHead")) {
    if (!officeArray || !presentOffice) return tempArray;
    
    const subordinateOffices = findSubordinateOffices(officeArray, presentOffice).map(x => x?.name || '');
    
    tempArray = tempArray.filter(user => {
      if (!user?.office) return false;

      const mainCharge = user.office.mainCharge || '';
      const additionalCharge = user.office.additionalCharge || [];
      const userOffices = [mainCharge, ...additionalCharge];

      return userOffices.some(office => subordinateOffices.concat(presentOffice).includes(office));
    });
    
    return tempArray;
  }
  
  return tempArray;
};



//This is a function which returns true if
// 1. If B is an Array there should be atleast one common element between A & B.
// 2. If B is String the Array A should have one of the element as that string.

const hasCommonElement = (A, B) => {
  if (!A || !Array.isArray(A)) return false;
  
  if (Array.isArray(B)) {
    if (B.length === 0) return false;
    // Check if there is at least one common element between arrays A and B
    return A.some(element => B.includes(element));
  } else if (typeof B === 'string' && B.length > 0) {
    // Check if array A contains the string B
    return A.includes(B);
  } else {
    // Handle other cases (e.g., B is neither an array nor a string)
    return false;
  }
}

export default function CustomPaginationActionsTable() {

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const location = useLocation();
  const [userdetailmodalopen, setUserDetailmodalopen] = useState(false);
  const [userdetailModaldata, setUserDetailModaldata] = useState({});
  const [assetsModalOpen, setAssetsModalOpen] = useState(false);
  const [assetsModalData, setAssetsModalData] = useState({});
  const [downloadData, setDownloadData] = useState({});
  const Auth = useAuth();
  const presentOffice = useSelector(state => state.office.off)


  const [searchData, setSearchData] = useState({
    name:"",
    email:"",
    designation:[],
    office:[],
    roles:[]
  }); 


  const {users} =  useGetUsersQuery("UserList", {
    selectFromResult: ({ data }) => ({
      users: data?.entities
    }),
  })

  const {designations} = useGetdesignationsQuery("DesigList", {
    selectFromResult: ({ data }) => ({
      designations: data?.entities
    }),
  })

  const {offices} = useGetofficesQuery("OffList", {
    selectFromResult: ({ data }) => ({
      offices: data?.entities
    }),
  })

  try {
    if (process.env.NODE_ENV !== 'production' && offices) {
      const subordinateOffices = findSubordinateOffices(Object.values(offices), "C.E (DSO)");
      console.log("Subordinate offices:", subordinateOffices);
    }
  } catch (error) {
    // Silent catch in production
  }

  const [tabledata, setTabledata]= useState([]);

  // console.log(useSelector(state => state.office.off))


  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tabledata.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUserDetailModalClose = () => {
    setUserDetailmodalopen(false);
  };

  const handleDetailsClick = (e, row)  => {
    setUserDetailModaldata(row);
    setUserDetailmodalopen(true)
  };

  const handleAssetsModalClose = () => {
    setAssetsModalOpen(false);
  };

  const handleAssetsClick = (e, row) => {
    setAssetsModalData(row);
    setAssetsModalOpen(true);
  };

 const handleSearchChange = (event, newValue, name) => {
  try {
    if (!users) return;
    
    let tempData = Object.values(users);
    let tempSearchData = structuredClone(searchData);
    
    if (event?.target?.name) {
      tempSearchData[event.target.name] = event.target.value;
      setSearchData(tempSearchData);
    } else if (name) {
      tempSearchData[name] = newValue;
      setSearchData(tempSearchData);
    }
    
    for (let key in tempSearchData) {
      if (typeof tempSearchData[key] === "string" && tempSearchData[key].length) {
        if (key === "name") {
          tempData = tempData.filter(x => {
            try {
              return nameStringFn(x).toLowerCase().includes(tempSearchData[key].toLowerCase());
            } catch (err) {
              return false;
            }
          });
        } else {
          tempData = tempData.filter(x => {
            try {
              return x[key]?.toLowerCase().includes(tempSearchData[key].toLowerCase());
            } catch (err) {
              return false;
            }
          });
        }
      }
      
      if (Array.isArray(tempSearchData[key]) && tempSearchData[key].length) {
        if (name === "designation" && tempSearchData[key][0]) {
          tempData = tempData.filter(x => {
            try {
              return x[key] && hasCommonElement(tempSearchData[key], Object.values(x[key]).flat(3));
            } catch (err) {
              return false;
            }
          });
        }
        if (name === "office" && tempSearchData[key][0]) {
          tempData = tempData.filter(x => {
            try {
              return x[key] && hasCommonElement(tempSearchData[key], Object.values(x[key]).flat(3));
            } catch (err) {
              return false;
            }
          });
        }
        if (name === "roles" && tempSearchData[key][0]) {
          tempData = tempData.filter(x => {
            try {
              return x[key] && hasCommonElement(tempSearchData[key], Object.values(x[key]).flat(3));
            } catch (err) {
              return false;
            }
          });
        }
      }
    }
    
    setTabledata(tempData.sort((a, b) => {
      try {
        // Add null checks to prevent localeCompare error
        const nameA = a?.name?.firstName?.toLowerCase() || '';
        const nameB = b?.name?.firstName?.toLowerCase() || '';
        return nameA.localeCompare(nameB); // Sort alphabetically
      } catch (err) {
        return 0;
      }
    }));
  } catch (error) {
    console.error('Error in handleSearchChange:', error);
  }
}

useEffect(() => {
  if(users && designations && offices){
    const userArray = structuredClone(Object.values(users));
    const officeArray = structuredClone(Object.values(offices));
    //Sorting based on Designation Level.
    setTabledata(filterFunctionUser(userArray, Auth, presentOffice,officeArray)
    .sort((a, b) => {
      // Add null checks to prevent localeCompare error
      const nameA = a?.name?.firstName?.toLowerCase() || '';
      const nameB = b?.name?.firstName?.toLowerCase() || '';
      return nameA.localeCompare(nameB); // Sort alphabetically
    })
  );
  }
},[users, designations, offices])


//Setting the download data for excel download.
useEffect(()=>{
  let finalDownloadData = [];
  let finalSortedDownloadData = [];
  const keyOrderArray = ["email", "name", "employeeID", "designation", "designationSuffix", "numSuffix", "office", "mobileNo", "officeLandLine", "officeAddress", "roles"]
  let tempDownloadData = structuredClone(tabledata);
  
  for(let val of tempDownloadData){
    if (!val) continue; // Skip if val is null or undefined
    
    let { _id, password, ...others } = val;
    
    others = {
      ...others,
      "name": nameStringFn(val),
      "designation": val?.designation ? Object.entries(val.designation).map(x => `${x[1]} (${x[0]}) `).join(", ") : "",
      "office": val?.office?.mainCharge || "",
      "designationSuffix": val?.designationSuffix ? Object.entries(val.designationSuffix).map(x => `${x[1]} (${x[0]}) `).join(",") : "",
      "numSuffix": val?.numSuffix ? Object.entries(val.numSuffix).map(x => `${x[1]} (${x[0]}) `).join(",") : "",
      "roles": val?.roles?.main ? `Main: (${val.roles.main.join(", ")}). Other: ${val?.roles ? Object.entries(val.roles).filter(x => x[0] !== "main").map(item => `${item[0]} (${item[1]})`).join(", ") : ""}` : ""
    };
    
    finalDownloadData.push(others);
  }

  for(let value of finalDownloadData){
    if (!value) continue; // Skip if value is null or undefined
    
    let tempObject = {};
    for(let val of keyOrderArray){
      tempObject = { ...tempObject, [val]: value[val] || "" };
    }
    finalSortedDownloadData.push(tempObject);
  }
  
  setDownloadData(finalSortedDownloadData);
}, [tabledata])

  return (
    <>    
      <HeadingWrap>
        <Span>My Users</Span>
      </HeadingWrap>
      <Button2Div>
          <ExcelDownload data={downloadData} filename={"myUsers"} sheetName={"sheet1"}/>
      </Button2Div> 
      <div>
      <TableContainer component={Paper} sx={{ backgroundColor: TableContStyle.backgroundColor }}>
      <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
      <TableHead  style={{backgroundColor: TableHeadStyle.backgroundColor }}>
        <TableRow >
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"5%"}}} >Sl.No.</TableCell>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"18%"}}} align="left">Name</TableCell>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"18%"}}}align="left">Email</TableCell>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"18%"}}} align="left">Designation</TableCell>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"18%"}}} align="left">Office</TableCell>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"18%"}}} align="left">Roles</TableCell>
          <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} sx={{width:{md:"15%"}}} align="left">Actions</TableCell>
        </TableRow>

        <TableRow  >
          <TableCell>
          </TableCell>

          <TableCell>
            <TextField sx={{background:"white"}}  name='name' onChange={handleSearchChange} label="Search Name" size="small"  variant="outlined" />
          </TableCell>

          <TableCell>
            <TextField sx={{background:"white"}}  name='email' onChange={handleSearchChange} label="Search Email" size="small"  variant="outlined" />
          </TableCell>
          <TableCell>

          <Autocomplete
              multiple
              id="checkboxes-tags-demo"
              onChange={(event, newValue) => handleSearchChange(event, newValue, "designation")}
              size="small"
              value={searchData.designation}
              options={designations? Object.values(designations).sort((a, b) => {  
                const difference =  a?.level- b.level ;
                return difference;
              }).map(x=>x.name):[]}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontSize: '2 rem' }}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 0, marginLeft:0 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Search Designation"  sx={{background:"white", width:"100%"}} />
              )}
              />
          </TableCell>
          <TableCell>

          <Autocomplete
              multiple
              id="checkboxes-tags-demo"
              onChange={(event, newValue) => handleSearchChange(event, newValue, "office")}
              size="small"
              value={searchData.office}
              //We set the options for available offices sorted by level and duplicacy removed.
              options={offices ? Object.values(offices).sort((a, b) => {  
                // Add null checks to prevent localeCompare error
                const levelA = a?.level || '';
                const levelB = b?.level || '';
                return levelA.localeCompare(levelB);
              }).map(x => x?.name || ''):[]}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontSize: '2 rem' }}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 0, marginLeft:0 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Search Offices"  sx={{background:"white", width:"100%"}} />
              )}
              />
          </TableCell>
          <TableCell>

          <Autocomplete
              multiple
              id="checkboxes-tags-demo"
              onChange={(event, newValue) => handleSearchChange(event, newValue, "roles")}
              size="small"
              value={searchData.roles}
              //We set the options for available offices sorted by level and duplicacy removed.
              options={users? ["Admin","unitHead", "Bo", "level1approver", "level2approver"].sort():[]}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontSize: '2 rem' }}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 0, marginLeft:0 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Search Roles"  sx={{background:"white", width:"100%"}} />
              )}
              />
          </TableCell>
          <TableCell>
          </TableCell>
        </TableRow>
      </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? tabledata.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : tabledata
          ).map((row,index) => (
            <TableRow key={index}>
              <TableCell component="th" scope="row" sx={{ fontSize: tableFontSize}}>
              {(page)*(rowsPerPage)+index+1}
              </TableCell>
              <TableCell  align="left" sx={{ fontSize: tableFontSize}}>
                {nameStringFn(row)}
              </TableCell>
              <TableCell  align="left" sx={{ fontSize: tableFontSize}}>
              {row?.email}
              </TableCell>
              <TableCell  align="left" sx={{ fontSize: tableFontSize}}>
              {row?.designation && row?.office?.mainCharge && row.designation[row.office.mainCharge] ? row.designation[row.office.mainCharge] : ''}
              </TableCell> 
              <TableCell  align="left" sx={{ fontSize: tableFontSize}}>
                {(() => {
                  const mainCharge = row?.office?.mainCharge || '';
                  const additionalCharges = row?.office?.additionalCharge || [];
                  
                  if (!mainCharge && additionalCharges.length === 0) return '';
                  
                  let displayText = mainCharge;
                  if (additionalCharges.length > 0) {
                    displayText += `, ${additionalCharges.join(', ')}`;
                  }
                  
                  return displayText;
                })()}
              </TableCell>              
              <TableCell align="left" sx={{ fontSize: tableFontSize, maxWidth:"200px"}}>
              {row?.roles ? userRolesArray(row).map((item, index) => <Chip sx={{margin:"2px"}} key={index} label={item} />) : null}
              </TableCell>
              <TableCell  align="left" sx={{ fontSize: tableFontSize}}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <DetailsIcon 
                    sx={{
                      color: "green", 
                      fontSize: "30px", 
                      '&:hover': {"cursor": "pointer"},
                      transition: "transform 0.2s",
                      '&:hover': { transform: "scale(1.1)" }
                    }} 
                    onClick={(e) => handleDetailsClick(e, row)} 
                    title="User Details"
                  />
                  <InventoryIcon 
                    sx={{
                      color: "blue", 
                      fontSize: "30px", 
                      '&:hover': {"cursor": "pointer"},
                      transition: "transform 0.2s",
                      '&:hover': { transform: "scale(1.1)" }
                    }} 
                    onClick={(e) => handleAssetsClick(e, row)} 
                    title="User Assets"
                  />
                </Box>
              </TableCell>

            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              colSpan={3}
              count={tabledata? tabledata?.length:[]}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
    </div>
    {userdetailmodalopen && <UserdetailsModal
      userdetailmodalopen = {userdetailmodalopen}
      userdetailModaldata = {userdetailModaldata}      
      handleUserDetailModalClose = {handleUserDetailModalClose}    
    />}
    {assetsModalOpen && <UserAssetsModal
      open={assetsModalOpen}
      userData={assetsModalData}
      onClose={handleAssetsModalClose}
    />}
    </>
  );

}